import { useState } from "react";
import { useTranslation } from "react-i18next";
import { changePassword, logout } from "../../lib/auth";

export function ChangePasswordTab() {
  const { t } = useTranslation();
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  function clearFields() {
    setOldPw("");
    setNewPw("");
    setConfirmPw("");
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    if (newPw.length < 8) {
      setError(
        t("settings.password.error_min_length", {
          defaultValue: "Neues Passwort muss mindestens 8 Zeichen lang sein.",
        }),
      );
      return;
    }
    if (newPw !== confirmPw) {
      setError(
        t("settings.password.error_mismatch", {
          defaultValue: "Bestätigung stimmt nicht mit neuem Passwort überein.",
        }),
      );
      return;
    }
    if (newPw === oldPw) {
      setError(
        t("settings.password.error_same", {
          defaultValue: "Neues Passwort darf nicht identisch mit dem alten sein.",
        }),
      );
      return;
    }
    setSubmitting(true);
    try {
      await changePassword(oldPw, newPw);
      setSuccess(true);
      clearFields();
      // Nach 3s automatisch ausloggen — der User soll sich frisch einloggen.
      setTimeout(() => {
        logout();
        window.location.href = "/login";
      }, 3000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(
        msg.includes("Altes Passwort")
          ? t("settings.password.error_wrong_old", {
              defaultValue: "Altes Passwort ist falsch.",
            })
          : msg,
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fv-card p-6 max-w-md space-y-4">
      <div>
        <h2 className="text-lg font-semibold">
          {t("settings.password.title", { defaultValue: "Passwort ändern" })}
        </h2>
        <p className="text-sm mt-1" style={{ color: "var(--muted)" }}>
          {t("settings.password.hint", {
            defaultValue:
              "Wird sofort wirksam und persistiert über Container-Restarts hinweg. Nach Erfolg wirst du automatisch ausgeloggt.",
          })}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <Field
          label={t("settings.password.old", { defaultValue: "Altes Passwort" })}
        >
          <input
            type="password"
            value={oldPw}
            onChange={(e) => setOldPw(e.target.value)}
            required
            autoComplete="current-password"
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{
              background: "var(--card)",
              border: "1px solid var(--border)",
              color: "var(--text)",
            }}
          />
        </Field>

        <Field
          label={t("settings.password.new", { defaultValue: "Neues Passwort" })}
          hint={t("settings.password.new_hint", {
            defaultValue: "Mindestens 8 Zeichen.",
          })}
        >
          <input
            type="password"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{
              background: "var(--card)",
              border: "1px solid var(--border)",
              color: "var(--text)",
            }}
          />
        </Field>

        <Field
          label={t("settings.password.confirm", {
            defaultValue: "Neues Passwort bestätigen",
          })}
        >
          <input
            type="password"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            required
            minLength={8}
            autoComplete="new-password"
            className="w-full px-3 py-2 rounded-lg text-sm"
            style={{
              background: "var(--card)",
              border: "1px solid var(--border)",
              color: "var(--text)",
            }}
          />
        </Field>

        {error && (
          <div
            className="text-sm px-3 py-2 rounded-lg"
            style={{ background: "var(--neg-soft)", color: "var(--neg)" }}
          >
            {error}
          </div>
        )}

        {success && (
          <div
            className="text-sm px-3 py-2 rounded-lg"
            style={{ background: "var(--pos-soft)", color: "var(--pos)" }}
          >
            {t("settings.password.success", {
              defaultValue:
                "Passwort geändert. Du wirst gleich ausgeloggt — bitte neu anmelden.",
            })}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || success}
          className="px-4 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50"
          style={{ background: "var(--accent)" }}
        >
          {submitting
            ? `${t("settings.password.submitting", { defaultValue: "Speichern" })}…`
            : t("settings.password.submit", { defaultValue: "Passwort ändern" })}
        </button>
      </form>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div
        className="text-xs font-semibold uppercase tracking-wider mb-1"
        style={{ color: "var(--muted)" }}
      >
        {label}
      </div>
      {children}
      {hint && (
        <div className="text-[11px] mt-1" style={{ color: "var(--muted)" }}>
          {hint}
        </div>
      )}
    </label>
  );
}
